import type { BannerConfig } from "./types";

const PANEL_SHADOWS: Record<"none" | "soft" | "strong", string> = {
	none: "none",
	soft: "0 24px 80px rgba(15, 23, 42, 0.22)",
	strong: "0 32px 96px rgba(15, 23, 42, 0.38)",
};

const BAR_SHADOWS: Record<"none" | "soft" | "strong", string> = {
	none: "none",
	soft: "0 -8px 32px rgba(15, 23, 42, 0.10)",
	strong: "0 -12px 48px rgba(15, 23, 42, 0.20)",
};

const SYSTEM_FONT_STACK =
	'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/**
 * Full stylesheet for the banner shadow root, derived from the resolved v2
 * config: extended colors, style knobs (radius / shadow / font) and layout.
 */
export function getStyles(config: BannerConfig): string {
	const { colors, style } = config;
	const border = colors.border || "rgba(15, 23, 42, 0.12)";
	const hairline = "rgba(15, 23, 42, 0.07)";
	const radius = Math.max(0, Math.min(32, style.radius));
	// Buttons keep a tighter radius, scaled from the panel radius (8px at the default 14).
	const btnRadius = Math.round((radius * 8) / 14);
	const panelShadow = PANEL_SHADOWS[style.shadow] ?? PANEL_SHADOWS.soft;
	const barShadow = BAR_SHADOWS[style.shadow] ?? BAR_SHADOWS.soft;
	const fontFamily = style.font === "inherit" ? "inherit" : SYSTEM_FONT_STACK;

	return `
    :host {
      all: initial;
      font-family: ${fontFamily};
      font-size: 14px;
      line-height: 1.55;
      color: ${colors.text};
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    button, a { font-family: inherit; }

    .cg-hidden { display: none !important; }

    /* ── Overlay (modal only) ── */
    .cg-overlay {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      z-index: 2147483646;
      opacity: 0;
      transition: opacity 250ms ease-out;
      pointer-events: auto;
    }
    .cg-overlay.cg-visible { opacity: 1; }

    /* ── Banner container ── */
    .cg-banner {
      position: fixed;
      z-index: 2147483647;
      background: ${colors.background};
      color: ${colors.text};
      pointer-events: auto;
      opacity: 0;
      transform: translateY(12px);
      transition: opacity 250ms ease-out, transform 250ms ease-out;
    }
    .cg-banner.cg-visible {
      opacity: 1;
      transform: translateY(0);
    }

    /* Modal: centered dialog */
    .cg-layout-modal {
      top: 50%;
      left: 50%;
      width: min(560px, calc(100vw - 32px));
      max-height: calc(100vh - 48px);
      overflow-y: auto;
      border-radius: ${radius}px;
      box-shadow: ${panelShadow};
      padding: 28px;
      transform: translate(-50%, calc(-50% + 12px)) scale(0.96);
    }
    .cg-layout-modal.cg-visible {
      transform: translate(-50%, -50%) scale(1);
    }

    /* Card: 384px corner card */
    .cg-layout-card-left,
    .cg-layout-card-right {
      bottom: 20px;
      width: min(384px, calc(100vw - 40px));
      max-height: calc(100vh - 40px);
      overflow-y: auto;
      border: 1px solid ${border};
      border-radius: ${radius}px;
      box-shadow: ${panelShadow};
      padding: 20px;
    }
    .cg-layout-card-left { left: 20px; }
    .cg-layout-card-right { right: 20px; }

    /* Narrow card: 2×2 category grid, stacked full-width buttons */
    .cg-layout-card-left .cg-cat-grid,
    .cg-layout-card-right .cg-cat-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .cg-layout-card-left .cg-cat-cell:not(:first-child),
    .cg-layout-card-right .cg-cat-cell:not(:first-child) { border-left: 0; }
    .cg-layout-card-left .cg-cat-cell:nth-child(even),
    .cg-layout-card-right .cg-cat-cell:nth-child(even) { border-left: 1px solid ${border}; }
    .cg-layout-card-left .cg-cat-cell:nth-child(n + 3),
    .cg-layout-card-right .cg-cat-cell:nth-child(n + 3) { border-top: 1px solid ${border}; }
    .cg-layout-card-left .cg-actions,
    .cg-layout-card-right .cg-actions { flex-direction: column; gap: 8px; }
    .cg-layout-card-left .cg-actions .cg-btn,
    .cg-layout-card-right .cg-actions .cg-btn { flex: 0 0 auto; width: 100%; }

    /* Bar: full-width strip */
    .cg-layout-bar-bottom,
    .cg-layout-bar-top {
      left: 0;
      right: 0;
      padding: 18px 24px;
    }
    .cg-layout-bar-bottom {
      bottom: 0;
      border-top: 1px solid ${border};
      box-shadow: ${barShadow};
    }
    .cg-layout-bar-top {
      top: 0;
      border-bottom: 1px solid ${border};
      box-shadow: ${
				style.shadow === "none" ? "none" : `0 8px 32px rgba(15, 23, 42, ${style.shadow === "strong" ? "0.20" : "0.10"})`
			};
      transform: translateY(-12px);
    }
    .cg-layout-bar-top.cg-visible { transform: translateY(0); }

    /* ── Header ── */
    .cg-header {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }
    .cg-logo {
      display: block;
      max-height: 28px;
      max-width: 120px;
      flex-shrink: 0;
    }
    .cg-title {
      font-size: 16px;
      font-weight: 700;
      letter-spacing: -0.01em;
    }

    /* ── Tabs ── */
    .cg-tabs {
      display: flex;
      gap: 22px;
      border-bottom: 1px solid ${border};
      margin: 14px 0 18px;
    }
    .cg-tab {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: ${colors.text};
      opacity: 0.55;
      padding: 10px 2px;
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;
      transition: opacity 150ms ease-out, color 150ms ease-out, border-color 150ms ease-out;
    }
    .cg-tab:hover { opacity: 0.85; }
    .cg-tab.cg-active {
      opacity: 1;
      color: ${colors.accent};
      border-bottom-color: ${colors.accent};
    }
    .cg-tab:focus-visible {
      outline: 2px solid ${colors.accent};
      outline-offset: 2px;
      border-radius: 3px;
    }

    /* ── Consent tab ── */
    .cg-description {
      font-size: 13.5px;
      opacity: 0.85;
    }
    .cg-readmore {
      display: inline-block;
      margin-top: 8px;
      font-size: 13px;
      color: ${colors.accent};
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .cg-readmore:hover { opacity: 0.8; }
    .cg-select-label {
      margin-top: 16px;
      font-size: 13px;
      font-weight: 600;
    }

    /* Category toggle grid: 4 columns, thin separators */
    .cg-cat-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      margin-top: 12px;
      border: 1px solid ${border};
      border-radius: ${Math.min(radius, 12)}px;
      background: ${colors.surface};
      overflow: hidden;
    }
    .cg-cat-cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding: 14px 8px;
    }
    .cg-cat-cell:not(:first-child) { border-left: 1px solid ${border}; }
    .cg-cat-name {
      font-size: 12.5px;
      font-weight: 600;
      text-align: center;
    }

    /* ── Toggle: 44×24, 18px knob ── */
    .cg-toggle {
      position: relative;
      display: inline-block;
      width: 44px;
      height: 24px;
      flex-shrink: 0;
    }
    .cg-toggle input {
      opacity: 0;
      width: 0;
      height: 0;
      position: absolute;
    }
    .cg-toggle-slider {
      position: absolute;
      cursor: pointer;
      inset: 0;
      background: #cbd5e1;
      border-radius: 24px;
      transition: background 200ms ease-out;
    }
    .cg-toggle-slider::before {
      content: "";
      position: absolute;
      height: 18px;
      width: 18px;
      left: 3px;
      top: 3px;
      background: #ffffff;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
      transition: transform 200ms ease-out;
    }
    .cg-toggle input:checked + .cg-toggle-slider { background: ${colors.accent}; }
    .cg-toggle input:checked + .cg-toggle-slider::before { transform: translateX(20px); }
    .cg-toggle input:disabled + .cg-toggle-slider {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .cg-toggle input:focus-visible + .cg-toggle-slider {
      outline: 2px solid ${colors.accent};
      outline-offset: 2px;
    }

    /* ── Buttons row: equal width, equal prominence ── */
    .cg-actions {
      display: flex;
      gap: 10px;
      margin-top: 18px;
    }
    /* Bar expanded panel: lone save-selection button, right-aligned */
    .cg-actions-save { justify-content: flex-end; }
    .cg-actions-save .cg-btn { flex: 0 0 auto; min-width: 180px; }
    .cg-btn {
      flex: 1 1 0;
      height: 40px;
      padding: 0 14px;
      border-radius: ${btnRadius}px;
      font-size: 13.5px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      transition: filter 150ms ease-out, background 150ms ease-out, transform 100ms ease-out;
    }
    .cg-btn:active { transform: scale(0.98); }
    .cg-btn:focus-visible {
      outline: 2px solid ${colors.accent};
      outline-offset: 2px;
    }
    .cg-btn-primary {
      background: ${colors.primary};
      color: ${colors.buttonText};
      border: 1px solid ${colors.primary};
    }
    .cg-btn-primary:hover { filter: brightness(1.08); }
    .cg-btn-outline {
      background: transparent;
      color: ${colors.text};
      border: 1px solid ${border};
    }
    .cg-btn-outline:hover { background: rgba(15, 23, 42, 0.04); }

    /* ── Powered-by ── */
    .cg-powered {
      margin-top: 12px;
      text-align: right;
      font-size: 11.5px;
      opacity: 0.6;
    }
    .cg-powered a {
      color: inherit;
      text-decoration: none;
    }
    .cg-powered a:hover {
      color: ${colors.accent};
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    /* ── Details tab ── */
    .cg-details {
      max-height: 320px;
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: rgba(15, 23, 42, 0.2) transparent;
      padding-right: 4px;
    }
    .cg-details::-webkit-scrollbar { width: 6px; }
    .cg-details::-webkit-scrollbar-thumb {
      background: rgba(15, 23, 42, 0.2);
      border-radius: 3px;
    }
    .cg-detail-group + .cg-detail-group { margin-top: 18px; }
    .cg-detail-heading {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      opacity: 0.7;
      margin-bottom: 6px;
    }
    .cg-detail-group-desc {
      font-size: 12.5px;
      opacity: 0.75;
    }
    .cg-cookie { padding: 8px 0; }
    .cg-cookie + .cg-cookie { border-top: 1px solid ${hairline}; }
    .cg-cookie-name {
      font-size: 13px;
      font-weight: 600;
    }
    .cg-cookie-desc {
      font-size: 12.5px;
      opacity: 0.75;
      margin-top: 2px;
    }

    /* ── About tab ── */
    .cg-about {
      max-height: 320px;
      overflow-y: auto;
      scrollbar-width: thin;
      padding-right: 4px;
    }
    .cg-about p {
      font-size: 13.5px;
      opacity: 0.85;
    }
    .cg-about p + p { margin-top: 10px; }
    .cg-about-cats { margin-top: 14px; }
    .cg-about-cat {
      padding: 9px 0;
      border-top: 1px solid ${hairline};
    }
    .cg-about-cat-label {
      font-size: 13px;
      font-weight: 600;
    }
    .cg-about-cat-desc {
      font-size: 12.5px;
      opacity: 0.75;
      margin-top: 2px;
    }
    .cg-about .cg-readmore { margin-top: 14px; }

    /* ── Bar compact row + inline expanded panel ── */
    .cg-bar-row {
      display: flex;
      align-items: center;
      gap: 24px;
      max-width: 1200px;
      margin: 0 auto;
    }
    .cg-bar-text {
      flex: 1 1 auto;
      min-width: 0;
    }
    .cg-bar-text .cg-description {
      font-size: 13px;
      margin-top: 2px;
    }
    .cg-bar-actions {
      display: flex;
      gap: 10px;
      flex-shrink: 0;
      align-items: center;
    }
    .cg-bar-actions .cg-btn {
      flex: 0 0 auto;
      padding: 0 18px;
    }
    .cg-panel {
      display: none;
      max-width: 1200px;
      margin: 0 auto;
    }
    .cg-panel.cg-open { display: block; }
    .cg-banner.cg-expanded .cg-bar-text .cg-description { display: none; }

    /* ── Responsive ── */
    @media (max-width: 640px) {
      .cg-layout-modal { padding: 20px; }
      .cg-cat-grid { grid-template-columns: repeat(2, 1fr); }
      .cg-cat-cell:not(:first-child) { border-left: none; }
      .cg-cat-cell:nth-child(even) { border-left: 1px solid ${border}; }
      .cg-cat-cell:nth-child(n + 3) { border-top: 1px solid ${border}; }
      .cg-bar-row {
        flex-direction: column;
        align-items: stretch;
        gap: 14px;
      }
      .cg-bar-actions { flex-wrap: wrap; }
      .cg-bar-actions .cg-btn { flex: 1 1 auto; }
      .cg-actions { flex-direction: column; }
      .cg-layout-bar-bottom, .cg-layout-bar-top { padding: 16px; }
    }

    /* ── Reduced motion ── */
    @media (prefers-reduced-motion: reduce) {
      .cg-banner,
      .cg-overlay,
      .cg-tab,
      .cg-btn,
      .cg-toggle-slider,
      .cg-toggle-slider::before {
        transition: none;
      }
    }
  `;
}
